import React from 'react';
import { Globe, ExternalLink, Loader2, Edit, Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ContactTab = ({ 
  countryContacts,
  contactsLoading,
  editingContact,
  setEditingContact,
  contactDialogOpen,
  setContactDialogOpen,
  saveCountryContact
}) => {
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Ülkelere Göre İletişim Bilgileri
              </CardTitle>
              <CardDescription>
                Her ülke için iletişim bilgilerini yönetin. Aktif olan ülkeler iletişim sayfasında görünür.
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => window.open('/contact', '_blank')} className="gap-2">
              <ExternalLink className="w-4 h-4" />
              İletişim Sayfasını Gör
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {contactsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-2">
              {/* Active Countries Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  <span className="font-semibold">{countryContacts.filter(c => c.is_active).length}</span> ülke aktif,{' '}
                  <span className="font-semibold">{countryContacts.filter(c => !c.is_active).length}</span> ülke pasif
                </p>
              </div>
              
              {/* Country List */}
              <div className="grid gap-2">
                {countryContacts.map((contact) => (
                  <div 
                    key={contact.country_code} 
                    className={`border rounded-lg p-4 transition-colors ${contact.is_active ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 opacity-75'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{contact.flag}</span>
                        <div>
                          <p className="font-medium">{contact.country_name}</p>
                          <p className="text-xs text-gray-500">{contact.country_code}</p>
                        </div>
                        {contact.is_active && (
                          <Badge className="bg-green-100 text-green-800 border-0">Aktif</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Quick Info */}
                        <div className="hidden md:flex items-center gap-4 text-sm text-gray-500">
                          {contact.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {contact.phone}
                            </span>
                          )}
                          {contact.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {contact.email}
                            </span>
                          )}
                        </div>
                        {/* Edit Button */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingContact({...contact});
                            setContactDialogOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Düzenle
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Edit Dialog */}
      <AlertDialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {editingContact?.flag} {editingContact?.country_name} - İletişim Bilgileri
            </AlertDialogTitle>
            <AlertDialogDescription>
              Bu ülke için iletişim bilgilerini düzenleyin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {editingContact && (
            <div className="space-y-4 py-4">
              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Durumu</p>
                  <p className="text-sm text-gray-500">Aktif ülkeler iletişim sayfasında görünür</p>
                </div>
                <Switch
                  checked={editingContact.is_active || false}
                  onCheckedChange={(v) => setEditingContact({...editingContact, is_active: v})}
                />
              </div>
              
              {/* Phone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  Telefon Numarası
                </Label>
                <Input
                  placeholder="+90 850 123 45 67"
                  value={editingContact.phone || ''}
                  onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                />
              </div>
              
              {/* WhatsApp */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  WhatsApp Numarası
                </Label>
                <Input
                  placeholder="+90 532 123 45 67"
                  value={editingContact.whatsapp || ''}
                  onChange={(e) => setEditingContact({...editingContact, whatsapp: e.target.value})}
                />
              </div>
              
              {/* Email */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  E-posta Adresi
                </Label>
                <Input
                  placeholder="destek@turkishemarket.com"
                  value={editingContact.email || ''}
                  onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                />
              </div>
              
              {/* Address */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-500" />
                  Adres
                </Label>
                <Textarea
                  placeholder="Ofis adresi..."
                  value={editingContact.address || ''}
                  onChange={(e) => setEditingContact({...editingContact, address: e.target.value})}
                  rows={2}
                />
              </div>
              
              {/* Working Hours */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-500" />
                  Çalışma Saatleri
                </Label>
                <Input
                  placeholder="Pazartesi-Cuma: 09:00-18:00"
                  value={editingContact.working_hours || ''}
                  onChange={(e) => setEditingContact({...editingContact, working_hours: e.target.value})}
                />
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setEditingContact(null);
              setContactDialogOpen(false);
            }}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction onClick={saveCountryContact}>
              Kaydet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ContactTab;
